// @ts-nocheck

import { colors } from '@lmc/common/constants'
import React, {
  type CSSProperties,
  type KeyboardEvent,
  type MutableRefObject,
  type RefObject,
  type SetStateAction,
  useRef,
  useState
} from 'react'

export type InputPinProps = {
  onComplete: (e: any) => void
  withVerify?: boolean
}

const styles = {
  container: {},
  input: {
    border: `1px solid ${colors.grey}`,
    borderRadius: 5,
    fontSize: 16, // if it's less than 16px, mobile browsers will zoom in!
    padding: 12,
    marginRight: 16,
    textAlign: 'center',
    width: 50
  },
  label: {
    fontWeight: 'bold',
    marginBottom: 4,
    paddingTop: 12
  }
}

const isNumeric = (input: string) => /^[0-9]*$/.test(input)

const ignored = [8, 9, 37, 39]

const isIgnored = (keyCode: number) => ignored?.includes(keyCode)

const isNumber = (keyCode: number) => keyCode > 47 && keyCode < 58

const onKeyDown = (event: { keyCode: number; preventDefault: () => void }) => {
  if (isIgnored(event?.keyCode)) return
  if (!isNumber(event?.keyCode)) event?.preventDefault()
}

const onKeyUp = (
  event: KeyboardEvent<HTMLInputElement>,
  onNext: { (): void; (): void }
) => {
  const element = event?.target as HTMLInputElement

  const { value } = element

  if (isIgnored(event?.keyCode)) return
  if (!isNumber(event?.keyCode) || !value)
    // eslint-disable-next-line consistent-return
    return event?.preventDefault()
  if (onNext) onNext()
  event?.preventDefault()
}

const handleComplete = (
  refs: any[],
  setShowWarning: {
    (value: SetStateAction<boolean>): void
    (arg0: boolean): void
  },

  onComplete: (arg0: any) => void,
  withVerify?: boolean
) => {
  const values = refs?.map((ref) => ref?.current?.value).join('')
  const pin = values?.substring(0, 4)
  const confirm = values?.substring(4, 8)
  if (withVerify) {
    if (pin?.length === 4 && pin === confirm) {
      setShowWarning(false)
      onComplete(pin)
    } else setShowWarning(true)
  } else {
    onComplete(pin)
  }
}

const focusNext = (refs: MutableRefObject<any>[], i: number) => {
  const nextRef = refs[i + 1]
  if (nextRef && nextRef?.current) {
    nextRef?.current?.focus()
  }
}

function InputPin({ onComplete, withVerify = false }: InputPinProps) {
  const [showWarning, setShowWarning] = useState<boolean>(false)
  let refs

  // do not render conditionally hooks!!!
  // this can break the app
  if (withVerify) {
    refs = [
      useRef(),
      useRef(),
      useRef(),
      useRef(),
      useRef(),
      useRef(),
      useRef(),
      useRef()
    ]
  } else {
    refs = [useRef(), useRef(), useRef(), useRef()]
  }

  const PinInputs = refs?.map((ref: RefObject<HTMLInputElement>, i) => (
    <input
      // eslint-disable-next-line react/no-array-index-key
      key={i}
      ref={ref}
      inputMode="numeric"
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={i === 0}
      style={styles?.input as CSSProperties}
      maxLength={1}
      onFocus={() => {
        // clear the value if it's already been filled in
        if (ref && ref?.current) ref.current.value = null
      }}
      onKeyUp={(e) => {
        if (!isNumeric(e.key)) {
          return e.preventDefault()
        }

        if (withVerify) {
          onKeyUp(e, () => {
            if (i === refs.length - 1)
              handleComplete(refs, setShowWarning, onComplete, false)
            else focusNext(refs, i)
          })
        } else {
          if (i !== refs.length - 1) {
            focusNext(refs, i)
          }
          handleComplete(refs, setShowWarning, onComplete, false)
        }
        return undefined
      }}
      onKeyDown={onKeyDown}
      onClick={onComplete}
    />
  ))

  return (
    <div style={styles.container}>
      <form>
        <div>{PinInputs?.splice(0, 4)}</div>
        {withVerify && (
          <div>
            <p style={styles.label as CSSProperties}>Confirm PIN</p>
            {PinInputs}
          </div>
        )}
        {showWarning && withVerify && <p>Your PIN codes need to match!</p>}
      </form>
    </div>
  )
}

export default InputPin
